import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeMassFamilyData } from './mass-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

function fmt(value, decimals = 6) {
  const number = numberOrNull(value);
  return number === null ? '—' : number.toFixed(decimals).replace('.', ',');
}

function dmy(value) {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '—';
}

async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function header(doc, { logo, number, page, pages, first, simulation = false }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first, simulation });
}

function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function labelValue(doc, label, value, x, y, valueX = x + 40) {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  doc.text(String(value ?? '—'), valueX, y);
}

function pageOne(doc, { session, instrument, familyData }) {
  let y = drawCertificateLegalText(doc, 67);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, y + 2);
  y += 11;
  doc.setFontSize(9.3);
  const left = [
    ['Objeto:', session.instrument_object || instrument?.type || 'Balanza monoplato'],
    ['Fabricante:', session.instrument_brand || instrument?.brand || '—'],
    ['Modelo:', session.instrument_model || instrument?.model || '—'],
    ['N.° de serie:', session.instrument_serial || instrument?.serial || '—'],
    ['TAG:', session.instrument_tag || instrument?.tag || '—'],
    ['Ubicación:', session.instrument_location || session.calibration_location || '—'],
    ['Intervalo:', `${fmt(session.instrument_range_min)} a ${fmt(session.instrument_range_max)} kg`],
  ];
  const right = [
    ['Determinación:', 'Calibración de masa'],
    ['Resolución:', `${fmt(session.instrument_resolution)} kg`],
    ['Tolerancia:', session.instrument_class == null ? '—' : `${fmt(session.instrument_class)} kg`],
    ['Clase:', familyData.balance_class || '—'],
    ['Calibración:', dmy(session.calibration_date)],
    ['Emisión del informe:', dmy(session.report_issue_date || new Date().toISOString())],
    ['Válido hasta:', dmy(session.valid_until)],
  ];
  left.forEach(([label, value], index) => labelValue(doc, label, value, 14, y + (index * 5.5), 47));
  right.forEach(([label, value], index) => labelValue(doc, label, value, 108, y + (index * 5.5), 144));
  y += 49;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CLIENTE', 14, y);
  y += 8;
  doc.setFontSize(9.3);
  [['Razón social:', session.client_name], ['Dirección:', session.client_address], ['Ciudad:', session.client_city], ['Provincia:', session.client_province]]
    .forEach(([label, value], index) => labelValue(doc, label, value || '—', 24, y + (index * 5.5), 57));
  y += 31;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, y);
  autoTable(doc, {
    startY: y + 6, margin: { left: 16, right: 16 }, theme: 'grid',
    styles: { fontSize: 8, halign: 'center', cellPadding: 1.7 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0] },
    head: [['Momento', 'Temperatura °C', 'Presión hPa', 'Humedad %HR']],
    body: familyData.environment_readings.map((row) => [row.label, fmt(row.temp_c, 1), fmt(row.pressure_hpa, 1), fmt(row.humidity_rh, 1)]),
  });
}

function rawRows(points = []) {
  const rows = {};
  points.forEach((point) => {
    const number = Number(point.point_no);
    const row = rows[number] || { point_no: number, setpoint: point.setpoint };
    row[String(point.direction || '').toLowerCase()] = point.instrument_reading;
    row.pattern = point.pattern_reading;
    rows[number] = row;
  });
  return Object.values(rows).sort((a, b) => a.point_no - b.point_no);
}

function pageTwo(doc, { session, points, results, familyData }) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('RESULTADOS OBTENIDOS DURANTE LA CALIBRACIÓN', 14, 40);
  autoTable(doc, {
    startY: 47, margin: { left: 7, right: 7 }, theme: 'grid',
    styles: { fontSize: 6.3, halign: 'center', cellPadding: 1.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0] },
    head: [['Pto.', 'Patrón kg', 'A1', 'A2', 'A3', 'A4', 'A5', 'B', 'C', 'D', 'E']],
    body: rawRows(points).map((row) => [row.point_no, fmt(row.pattern, 9), ...['a1', 'a2', 'a3', 'a4', 'a5', 'b', 'c', 'd', 'e'].map((key) => fmt(row[key]))]),
  });
  const detailMap = Object.fromEntries((familyData.result_details || []).map((row) => [Number(row.point_no), row]));
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || 110) + 9, margin: { left: 7, right: 7 }, theme: 'grid',
    styles: { fontSize: 6.8, halign: 'center', cellPadding: 1.3 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0] },
    head: [['Pto.', 'Patrón kg', 'Instrumento kg', 'Error kg', 'Corrección kg', 'Repetibilidad', 'Excentricidad', 'U (k=2) kg', 'Conforme']],
    body: [...results].sort((a, b) => a.point_no - b.point_no).map((result) => {
      const detail = detailMap[Number(result.point_no)] || {};
      return [
        result.point_no,
        fmt(result.pattern_mean, 9),
        fmt(result.instrument_mean),
        fmt(result.error_abs),
        fmt(detail.correction ?? -Number(result.error_abs || 0)),
        fmt(result.repeatability),
        fmt(detail.eccentricity_error ?? result.hysteresis),
        fmt(result.u_reported),
        result.in_tolerance == null ? '—' : result.in_tolerance ? 'Sí' : 'No',
      ];
    }),
  });
  const y = (doc.lastAutoTable?.finalY || 180) + 10;
  const maxAbs = (values) => {
    const finite = values.map(numberOrNull).filter((value) => value !== null).map(Math.abs);
    return finite.length ? Math.max(...finite) : null;
  };
  const detailFor = (result) => detailMap[Number(result.point_no)] || {};
  const summaries = [
    ['Máximo error de indicación:', maxAbs(results.map((result) => result.error_abs))],
    ['Incertidumbre expandida máxima:', maxAbs(results.map((result) => result.u_reported))],
    ['Máximo error de excentricidad:', maxAbs(results.map((result) => detailFor(result).eccentricity_error ?? result.hysteresis))],
    ['Máxima repetibilidad:', maxAbs(results.map((result) => result.repeatability))],
  ];
  doc.setFontSize(8.8);
  summaries.forEach(([label, value], index) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 14, y + (index * 5));
    doc.setFont('helvetica', 'normal');
    doc.text(`${fmt(value)} kg`, 91, y + (index * 5), { align: 'right' });
  });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Error del instrumento = promedio de A1-A5 - valor convencional de la masa patrón.', 14, y + 25);
  doc.text('Corrección = -error. Conformidad con banda de guarda: |error| + U <= tolerancia.', 14, y + 31);
}

function pageThree(doc, { session, familyData, signature }) {
  let y = 41;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.2);
  doc.text(familyData.method, 14, y + 7);
  doc.text(familyData.procedure, 14, y + 13);
  y += 24;
  const weights = familyData.standard_configuration?.weights || [];
  const massReferences = weights.map((snapshot) => {
      const result = snapshot.results?.[0] || {};
      const nominal = snapshot.capability?.metadata?.nominal_kg ?? result.reference_value;
      const uncertaintyMg = numberOrNull(result.uncertainty_expanded) === null
        ? null
        : Number(result.uncertainty_expanded) * 1_000_000;
      return {
        ...snapshot,
        usage: 'Masa patrón',
        scope: `${fmt(nominal, 9)} kg · valor ${fmt(result.indicated_value, 9)} kg · U ${fmt(uncertaintyMg, 3)} mg`,
      };
    });
  drawReferenceStandardsTable(doc, {
    startY: y,
    references: massReferences,
    margin: { left: 9, right: 9 },
    fontSize: 6.2,
  });
  y = (doc.lastAutoTable?.finalY || y) + 9;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('PRESUPUESTO DE INCERTIDUMBRE', 14, y);
  autoTable(doc, {
    startY: y + 5, margin: { left: 14, right: 14 }, theme: 'grid',
    styles: { fontSize: 7.6, cellPadding: 1.5 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0] },
    head: [['Componente', 'Evaluación']],
    body: [
      ['Calibración de las masas patrón', 'U/k de cada certificado; contribuciones correlacionadas sumadas linealmente'],
      ['Deriva de las masas patrón', 'D/sqrt(3) con factor de deriva documentado'],
      ['Resolución sin carga y con carga', 'd/(2 sqrt(3)), combinación cuadrática'],
      ['Repetibilidad', 'Desviación estándar de la media: s/sqrt(5)'],
      ['Excentricidad', 'Máxima diferencia respecto del centro / (2 sqrt(3))'],
      ['Empuje del aire y convección', 'Según P-DT-PT-25-00'],
      ['Incertidumbre expandida', 'U = 2 uc, nivel de confianza cercano al 95,45 %'],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('OBSERVACIONES', 14, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const observation = session.notes || 'La balanza fue calibrada por comparación directa con masas patrón certificadas.';
  doc.text(doc.splitTextToSize(observation, 180), 14, y + 7);
  if (signature) doc.addImage(signature, 'PNG', 78, 238, 44, 29, undefined, 'FAST');
}

export async function generateMassNoSacCertificatePdf({ session, instrument, points = [], results = [] }, options = {}) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([
    imageAsDataUrl(options.logoUrl || LOGO_URL), imageAsDataUrl(options.signatureUrl || SIGNATURE_URL),
  ]);
  const number = certificateNumber(options.serviceOrderCode, session.service_order_code, session.certificate_number, instrument?.lab_id);
  const familyData = mergeMassFamilyData(session.family_data);
  pageOne(doc, { session, instrument, familyData });
  doc.addPage();
  pageTwo(doc, { session, points, results, familyData });
  doc.addPage();
  pageThree(doc, { session, familyData, signature });
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    header(doc, { logo, number, page, pages, first: page === 1, simulation: options.simulation === true });
    footer(doc, page, pages);
  }
  if (options.returnBlob) return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  doc.save(options.filename || `${number !== '—' ? number : 'R-DT-PC-MASS'}.pdf`);
  return null;
}

